import { Check, ChevronDown, ChevronUp, Minus, X } from 'lucide-react';

import { Keycap } from '../../common/index.jsx';

/**
 * The active-decision panel from the old tab: one pipeline span (or missing detail) at
 * a time, confirm / reject / wrong-label, with auto-advance.
 *
 * Keys differ from the old tool in one place. There, left/right moved through the
 * queue; here left/right move between IMAGES, which is what was asked for, so the
 * queue uses n/p. Up/down still confirm and reject.
 */
export function DecisionQueue({
  queue,
  activeIndex,
  decisions,
  detailDecisions,
  minimized,
  onMinimize,
  onMove,
  onDecide,
  onNote,
}) {
  if (!queue.length) {
    return <div className="empty-state">This run proposed no spans or missing details.</div>;
  }

  const item = queue[activeIndex] || queue[0];
  const byIndex = item.kind === 'span' ? decisions : detailDecisions;
  const current = byIndex[item.index]?.decision;

  const pending = queue.filter((q) => {
    const map = q.kind === 'span' ? decisions : detailDecisions;
    return !map[q.index]?.decision;
  }).length;

  if (minimized) {
    return (
      <div className="minimized-decision-row">
        <span className="queue-position">
          {activeIndex + 1}/{queue.length}
        </span>
        <span className="grow muted">{item.text}</span>
        <span className="chip">{pending} pending</span>
        <button type="button" className="btn sm" onClick={onMinimize}>
          Expand <Keycap>m</Keycap>
        </button>
      </div>
    );
  }

  return (
    <div
      className={`active-decision-card inline-decision-panel${
        item.kind === 'missing' ? ' missing' : ''
      }`}
    >
      <div className="active-decision-top">
        <span className="queue-position">
          {activeIndex + 1}/{queue.length}
        </span>
        <strong>{item.kind === 'span' ? 'Span decision' : 'Missing detail'}</strong>
        <div className="active-decision-nav">
          <button type="button" className="btn sm" onClick={() => onMove(-1)}>
            Prev <Keycap>p</Keycap>
          </button>
          <button type="button" className="btn sm" onClick={() => onMove(1)}>
            Next <Keycap>n</Keycap>
          </button>
          <button type="button" className="btn sm ghost" onClick={onMinimize}>
            <Minus size={12} /> <Keycap>m</Keycap>
          </button>
        </div>
      </div>

      <div className="active-decision-line">
        <span className="active-decision-text">{item.text}</span>
      </div>

      <div className="active-decision-meta">
        {item.label && (
          <span className={`final-pill${item.kind === 'missing' ? ' detail-kind' : ''}`}>
            {item.label.replaceAll('_', ' ')}
          </span>
        )}
        {item.action && <span className="final-pill">{item.action.replaceAll('_', ' ')}</span>}
        {item.range && !item.range.exact && (
          <span className="final-pill approximate" title="Located by a loose match">
            approximate match
          </span>
        )}
        {!item.range && item.kind === 'span' && (
          <span className="final-pill not_found" title="This span text was not found in the caption">
            not located
          </span>
        )}
        {item.replacement && (
          <span className="muted">
            → <strong>{item.replacement}</strong>
          </span>
        )}
        {item.uncertaintyNote && <span className="muted">note: {item.uncertaintyNote}</span>}
      </div>

      {item.reason && <p className="final-reason">{item.reason}</p>}

      <div className="quick-decision-row">
        <button
          type="button"
          className={`btn confirm ${current === 'confirmed' ? 'active' : ''}`}
          onClick={() => onDecide('confirmed')}
        >
          <Check size={13} /> Confirm <Keycap>↑</Keycap>
        </button>
        <button
          type="button"
          className={`btn reject ${current === 'rejected' ? 'active' : ''}`}
          onClick={() => onDecide('rejected')}
        >
          <X size={13} /> Reject <Keycap>↓</Keycap>
        </button>
        {item.kind === 'span' && (
          <button
            type="button"
            className={`btn needs-correction ${current === 'wrong_label_or_action' ? 'active' : ''}`}
            title="The span is a real problem, but the agent's label, action or replacement is wrong."
            onClick={() => onDecide('wrong_label_or_action')}
          >
            Wrong action/label/replacement <Keycap>x</Keycap>
          </button>
        )}
        <span className="muted">{pending} pending</span>
      </div>

      <input
        className="input sm"
        placeholder="Human notes on this decision"
        value={byIndex[item.index]?.human_notes || ''}
        onChange={(e) => onNote(e.target.value)}
        aria-label="Human notes"
      />

      <div className="shortcut-hint">
        <Keycap>p</Keycap>/<Keycap>n</Keycap> previous / next · <Keycap>↑</Keycap> confirm ·{' '}
        <Keycap>↓</Keycap> reject · <Keycap>x</Keycap> wrong label · <Keycap>m</Keycap> minimize
      </div>
    </div>
  );
}

/**
 * What each highlight colour in the caption means.
 *
 * Carried over from the old tool, including the split that matters: a span verdict is
 * green/red/amber, while a verified missing detail - text the run ADDED rather than a
 * fault it found - is cyan or pink. Without the legend the two families read as one.
 */
const DECISION_LEGEND = [
  ['agent-pending', 'Pending decision'],
  ['agent-confirmed', 'Confirmed'],
  ['agent-rejected', 'Rejected'],
  ['agent-wrong_label_or_action', 'Wrong label/action'],
  ['agent-detail-pending agent-detail', 'Missing detail'],
  ['agent-detail-confirmed', 'Missing detail confirmed'],
  ['agent-detail-rejected', 'Missing detail rejected'],
];

export function DecisionLegend() {
  return (
    <div className="legend decision-legend">
      {DECISION_LEGEND.map(([cls, label]) => (
        <span className="legend-item" key={cls}>
          <span className={`legend-swatch ${cls}`} />
          {label}
        </span>
      ))}
    </div>
  );
}

/** The full list, as the old "All final span decisions" disclosure. */

export function DecisionList({ queue, decisions, detailDecisions, onJump, onDecide, onNote }) {
  const spans = queue.filter((q) => q.kind === 'span');
  const details = queue.filter((q) => q.kind === 'missing');

  return (
    <>
      <details className="disclosure" open>
        <summary>All final span decisions ({spans.length})</summary>
        <div className="decision-list">
          {spans.map((item) => {
            const decision = decisions[item.index]?.decision || '';
            return (
              <article className={`decision-card ${decision}`} key={item.id}>
                <div className="decision-card-head">
                  <button type="button" className="link-button" onClick={() => onJump(item.id)}>
                    <strong>{item.text}</strong>
                  </button>
                  <select
                    className="select sm"
                    style={{ width: 'auto' }}
                    value={decision}
                    onChange={(e) => onDecide(item, e.target.value)}
                  >
                    <option value="">Pending</option>
                    <option value="confirmed">Confirm</option>
                    <option value="rejected">Reject</option>
                    <option value="wrong_label_or_action">Wrong action/label/replacement</option>
                  </select>
                </div>
                <div className="readonly-decision-grid">
                  <span>Agent label</span><strong>{item.label || '-'}</strong>
                  <span>Agent action</span><strong>{item.action || '-'}</strong>
                  <span>Agent replacement</span><strong>{item.replacement || '-'}</strong>
                  <span>Agent uncertainty note</span><strong>{item.uncertaintyNote || '-'}</strong>
                </div>
                <input
                  className="input sm"
                  placeholder={item.reason || 'Human notes'}
                  value={decisions[item.index]?.human_notes || ''}
                  onChange={(e) => onNote(item, e.target.value)}
                  aria-label={`Human notes for ${item.text}`}
                />
              </article>
            );
          })}
        </div>
      </details>

      <details className="disclosure">
        <summary>All verified missing details ({details.length})</summary>
        <div className="decision-list">
          {details.map((item) => {
            const decision = detailDecisions[item.index]?.decision || '';
            return (
              <article className={`decision-card missing ${decision}`} key={item.id}>
                <div className="decision-card-head">
                  <button type="button" className="link-button" onClick={() => onJump(item.id)}>
                    <strong>{item.text}</strong>
                  </button>
                  <select
                    className="select sm"
                    style={{ width: 'auto' }}
                    value={decision}
                    onChange={(e) => onDecide(item, e.target.value)}
                  >
                    <option value="">Pending</option>
                    <option value="confirmed">Confirm</option>
                    <option value="rejected">Reject</option>
                  </select>
                </div>
                <div className="readonly-decision-grid">
                  <span>Agent missing detail</span><strong>{item.text}</strong>
                </div>
                <input
                  className="input sm"
                  placeholder="Human notes"
                  value={detailDecisions[item.index]?.human_notes || ''}
                  onChange={(e) => onNote(item, e.target.value)}
                  aria-label={`Human notes for ${item.text}`}
                />
              </article>
            );
          })}
        </div>
      </details>
    </>
  );
}

/** The `.adjudication-stats` row. */
export function AdjudicationStats({ queue, decisions, detailDecisions }) {
  const spans = queue.filter((q) => q.kind === 'span');
  const details = queue.filter((q) => q.kind === 'missing');
  const count = (items, map, value) =>
    items.filter((i) => (map[i.index]?.decision || 'pending') === value).length;

  return (
    <div className="adjudication-stats">
      <span><strong>{count(spans, decisions, 'confirmed')}</strong> span decisions confirmed</span>
      <span><strong>{count(spans, decisions, 'rejected')}</strong> rejected</span>
      <span>
        <strong>{count(spans, decisions, 'wrong_label_or_action')}</strong> wrong action/label/replacement
      </span>
      <span><strong>{count(spans, decisions, 'pending')}</strong> pending</span>
      <span>
        <strong>{count(details, detailDecisions, 'confirmed')}</strong> missing details confirmed
      </span>
    </div>
  );
}
